import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { Image, Skeleton } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';

import { useStores } from '@/hooks/use-stores';
import { ImageEditPreview } from '@/comp/Forms/SiteSurveyForm/RoomConfigurationStep/FormComponents/CompressorRow/components';
import { COMPRESSOR_IMAGE_TAG_DIRECTORY_DATA } from '@/constants/CompressorImageTagData.const';
import { useResponsive } from '@/hooks/useResponsive';

import { ImageListContainer } from './ImageList.styles';
import ImageUpload from '../ImageUpload/ImageUpload';
import UploadPicturesMobile from './UploadPicturesMobile/UploadPicturesMobile';
import UploadPicturesDesktop from './UploadPicturesDesktop/UploadPicturesDesktop';
import { ImageUploadButtons } from '@/comp/Forms/SiteSurveyForm/RoomConfigurationStep/FormComponents/ImageUpload/components';
import { useImageCompression } from '@/comp/Forms/SiteSurveyForm/RoomConfigurationStep/FormComponents/ImageUpload/hooks';
import { MAX_IMAGE_UPLOAD_COUNT } from '@/comp/Forms/SiteSurveyForm/RoomConfigurationStep/FormComponents/ImageUpload/ImageUpload.consts';

const ImageList = observer(({ deviceId }: { deviceId: string }) => {
  const { t } = useTranslation();
  const { devicesStore, imageUploadStore } = useStores();
  const { isMobile } = useResponsive();
  const params = useImageCompression({ onComplete: imageUploadStore.goToNextStep });
  const hasReachedMaxImages = devicesStore.deviceImages?.length >= MAX_IMAGE_UPLOAD_COUNT;

  const [isVisible, setIsVisible] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const closePreview = () => setIsVisible(false);

  const openEditPreview = (imageIndex: number) => () => {
    setIsVisible(true);
    setCurrentImageIndex(imageIndex);
  };

  const onEditTag = async (imageId: string, newTag: string) => {
    await devicesStore.changeTag(imageId, newTag);
    setIsVisible(false);
    devicesStore.getDeviceImages(deviceId);
  };

  const onDeleteImage = async (imageId: string) => {
    await devicesStore.deleteImage(imageId);
    setIsVisible(false);
    devicesStore.getDeviceImages(deviceId);
    imageUploadStore.setMaxFiles(imageUploadStore.maxFiles + 1);
  };

  return (
    <ImageListContainer $isMobile={isMobile}>
      {imageUploadStore.currentStep === 0 ? (
        <>
          {isMobile ? <UploadPicturesMobile /> : <UploadPicturesDesktop />}
          <span className="images-available-count">
            {t('picturesAvailableCount', { count: devicesStore.deviceImages?.length })}
          </span>
          <div className="image-list">
            {devicesStore.deviceImages?.map((image, index) => (
              <div key={image.id} className="image-item" onClick={openEditPreview(index)}>
                <Image
                  src={image.url}
                  key={image.id}
                  preview={{ mask: <DownloadOutlined className="download-icon" />, visible: false }}
                  width={146}
                  height={146}
                  placeholder={<Skeleton.Image style={{ width: 146, height: 146 }} />}
                />
                <p>{t(COMPRESSOR_IMAGE_TAG_DIRECTORY_DATA[image.tag]?.translationKey || '')}</p>
              </div>
            ))}
          </div>
          {isMobile && (
            <div className="image-upload-buttons-container">
              <ImageUploadButtons buttonText="uploadPictures" isDisabled={hasReachedMaxImages} {...params} />
            </div>
          )}
          <ImageEditPreview
            images={devicesStore.deviceImages}
            setCurrentImageIndex={setCurrentImageIndex}
            onRemoveCompressorImage={onDeleteImage}
            currentImageIndex={currentImageIndex}
            visible={isVisible}
            onEditTag={onEditTag}
            closePreview={closePreview}
            mapView
          />
        </>
      ) : (
        <ImageUpload deviceId={deviceId} />
      )}
    </ImageListContainer>
  );
});

export default ImageList;
